// src/components/profile/ProfileDropdown.js
import React, { useState, useRef, useEffect } from "react";
import {
  Camera,
  User,
  Settings,
  Moon,
  LogOut,
  Linkedin,
  X,
  Globe,
  Edit,
  Trash2
} from "lucide-react";
import "./ProfileDropdown.css";

const Modal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{title}</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const ProfileAvatar = ({
  src,
  username,
  size = "md",
  isEditable = false,
  onImageChange,
  onImageRemove
}) => {
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (JPEG, PNG, or GIF)');
        return;
      }

      if (file.size > maxSize) {
        alert('Image size should be less than 5MB');
        return;
      }

      if (onImageChange) {
        onImageChange(file);
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`profile-avatar-container ${size}`}>
      <div className="profile-avatar">
        {src ? (
          <img src={src} alt={`${username}'s profile`} className="profile-avatar-image" />
        ) : (
          <div className="profile-avatar-placeholder">
            {username?.charAt(0)?.toUpperCase() || <User size={24} />}
          </div>
        )}
      </div>

      {isEditable && (
        <div className="profile-avatar-actions">
          <button
            className="avatar-action-button upload"
            onClick={triggerFileInput}
            title="Upload new picture"
          >
            <Camera size={16} />
          </button>
          {src && (
            <button
              className="avatar-action-button remove"
              onClick={onImageRemove}
              title="Remove picture"
            >
              <Trash2 size={16} />
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/jpeg,image/png,image/gif"
            onChange={handleImageChange}
          />
        </div>
      )}
    </div>
  );
};

const TabButton = ({ active, onClick, children }) => (
  <button
    className={`tab-button ${active ? 'active' : ''}`}
    onClick={onClick}
  >
    {children}
  </button>
);

const ProfileDropdown = ({ user, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const dropdownRef = useRef(null);
  const [profileData, setProfileData] = useState({
    username: user?.username || "",
    bio: "",
    location: "",
    website: "",
    linkedinUrl: "",
    twitterUrl: "",
    organization: {
      type: "company",
      name: "",
      role: ""
    },
    email: user?.emailOrPhone || ""
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({
        username: user.username || "",
        bio: user.bio || "",
        location: user.location || "",
        website: user.website || "",
        linkedinUrl: user.linkedinUrl || "",
        twitterUrl: user.twitterUrl || "",
        organization: {
          type: user.organization?.type || "company",
          name: user.organization?.name || "",
          role: user.organization?.role || ""
        },
        email: user.emailOrPhone || ""
      });
    }
  }, [user]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleImageChange = (file) => {
    if (file) {
      setSelectedImage(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleImageRemove = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setProfileData(prev => ({
      ...prev,
      profilePicture: null
    }));
  };

  const handleLogout = async () => {
    try {
      const response = await fetch(`http://34.47.145.31:8000/api/auth/logout`, {
        credentials: "include",
      });

      if (response.ok) {
        onNavigate("/login");
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    Object.keys(profileData).forEach((key) => {
      if (key !== 'organization' && key !== 'email') {
        formData.append(key, profileData[key]);
      }
    });

    formData.append('organizationType', profileData.organization.type);
    formData.append('organizationName', profileData.organization.name);
    formData.append('organizationRole', profileData.organization.role);

    if (selectedImage) {
      formData.append("profileImage", selectedImage);
    }

    try {
      const response = await fetch(`http://34.47.145.31:8000/api/profile`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        setIsEditModalOpen(false);
        window.location.reload();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert('Failed to update profile. Please try again.');
    }
  };

  return (
    <div className="profile-container" ref={dropdownRef}>
      <button
        className="profile-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Profile menu"
      >
        <ProfileAvatar
          src={user?.profilePicture}
          username={user?.username}
          size="sm"
        />
      </button>

      {isOpen && (
        <div className="profile-menu">
          <div className="profile-header">
            <div className="profile-info">
              <ProfileAvatar
                src={user?.profilePicture}
                username={user?.username}
                size="md"
              />
              <div className="user-info">
                <span className="username">{user?.username}</span>
                <span className="email">{user?.emailOrPhone}</span>
              </div>
            </div>
            <div className="profile-stats">
              <div className="stat-item">
                <span className="stat-value">{user?.followers?.length || 0}</span>
                <span className="stat-label">Followers</span>
              </div>
              <div className="stat-divider" />
              <div className="stat-item">
                <span className="stat-value">{user?.following?.length || 0}</span>
                <span className="stat-label">Following</span>
              </div>
            </div>
          </div>

          <div className="profile-actions">
            <button className="action-button" onClick={() => {
              setIsEditModalOpen(true);
              setIsOpen(false);
            }}>
              <Edit size={16} />
              <span>Edit Profile</span>
            </button>
            <button className="action-button" onClick={() => onNavigate("/settings")}>
              <Settings size={16} />
              <span>Settings</span>
            </button>
            <button className="action-button" onClick={() => {
              document.body.classList.toggle('dark-theme');
            }}>
              <Moon size={16} />
              <span>Toggle Theme</span>
            </button>
            <button className="action-button logout" onClick={handleLogout}>
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Profile"
      >
        <div className="edit-profile">
          <div className="edit-profile-container">
            <div className="profile-image-edit">
              <ProfileAvatar
                src={imagePreview || user?.profilePicture}
                username={user?.username}
                size="lg"
                isEditable={true}
                onImageChange={handleImageChange}
                onImageRemove={handleImageRemove}
              />
              <p className="image-upload-hint">
                Click the camera icon to upload a new profile picture
                <br />
                <small>Supported formats: JPEG, PNG, GIF (max 5MB)</small>
              </p>
            </div>

            <div className="tab-buttons">
              <TabButton
                active={activeTab === 'basic'}
                onClick={() => setActiveTab('basic')}
              >
                Basic Info
              </TabButton>
              <TabButton
                active={activeTab === 'social'}
                onClick={() => setActiveTab('social')}
              >
                Social Links
              </TabButton>
            </div>

            <form onSubmit={handleSubmit} className="edit-form">
              {activeTab === 'basic' ? (
                <div className="form-section">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={profileData.username}
                      onChange={(e) =>
                        setProfileData({ ...profileData, username: e.target.value })
                      }
                      className="form-input"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={profileData.email}
                      className="form-input"
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label>Bio</label>
                    <textarea
                      value={profileData.bio}
                      onChange={(e) =>
                        setProfileData({ ...profileData, bio: e.target.value })
                      }
                      className="form-textarea"
                      placeholder="Tell us about yourself"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={profileData.location}
                      onChange={(e) =>
                        setProfileData({ ...profileData, location: e.target.value })
                      }
                      className="form-input"
                      placeholder="Where are you based?"
                    />
                  </div>

                  <div className="organization-section">
                    <div className="form-group">
                      <label>Organization Type</label>
                      <select
                        value={profileData.organization.type}
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            organization: {
                              ...profileData.organization,
                              type: e.target.value
                            }
                          })
                        }
                        className="form-select"
                      >
                        <option value="company">Company</option>
                        <option value="college">College</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>
                        {profileData.organization.type === 'company' ? 'Company' : 'College'} Name
                      </label>
                      <input
                        type="text"
                        value={profileData.organization.name}
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            organization: {
                              ...profileData.organization,
                              name: e.target.value
                            }
                          })
                        }
                        className="form-input"
                        placeholder={`Enter ${profileData.organization.type.toLowerCase()} name`}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        {profileData.organization.type === 'company' ? 'Job Title' : 'Course/Major'}
                      </label>
                      <input
                        type="text"
                        value={profileData.organization.role}
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            organization: {
                              ...profileData.organization,
                              role: e.target.value
                            }
                          })
                        }
                        className="form-input"
                        placeholder={`Enter your ${profileData.organization.type === 'company'
                            ? 'job title'
                            : 'course/major'
                          }`}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="form-section">
                  <div className="form-group">
                    <label>Website</label>
                    <div className="input-with-icon">
                      <Globe size={16} />
                      <input
                        type="url"
                        value={profileData.website}
                        onChange={(e) =>
                          setProfileData({ ...profileData, website: e.target.value })
                        }
                        className="form-input"
                        placeholder="https://your-website.com"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>LinkedIn</label>
                    <div className="input-with-icon">
                      <Linkedin size={16} />
                      <input
                        type="url"
                        value={profileData.linkedinUrl}
                        onChange={(e) =>
                          setProfileData({ ...profileData, linkedinUrl: e.target.value })
                        }
                        className="form-input"
                        placeholder="LinkedIn profile URL"
                      /></div>
                  </div>

                  <div className="form-group">
                    <label>Twitter</label>
                    <div className="input-with-icon">
                      <X size={16} />
                      <input
                        type="url"
                        value={profileData.twitterUrl}
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            twitterUrl: e.target.value,
                          })
                        }
                        className="form-input"
                        placeholder="Twitter profile URL"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProfileDropdown;